import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';
import {
  ScreeningKanBanStatusEnum,
  ScreeningRestrictionItem,
} from '@/app/common/general-types/social-screening.dto';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { ImageService } from '@/app/services/image/image.service';
import { VideoAiModerateService } from '@/app/services/video-ai-moderate/video-ai-moderate.service';
import {
  daysBetween,
  formatScreeningDay,
  oneYearBefore,
  startOfLocalDay,
  timelineOffset,
} from '@/app/pages/screening-brief-select/sections/screening-window/screening-window.helpers';
import { ReviewQuestionsDrawer } from '../../atoms/review-questions-drawer/review-questions-drawer';
import { ReviewQuestionsModal } from '../../atoms/review-questions-modal/review-questions-modal';
import { ScreeningResultsService } from '../../services/screening-results/screening-results.service';

export type RuleFilter = 'all' | 'do' | 'dont' | 'objective' | 'subjective';

interface RuleFilterChip {
  id: RuleFilter;
  label: string;
  count: number;
}

interface RestrictionGroup {
  label: string;
  items: ScreeningRestrictionItem[];
}

/**
 * Tab 1 — the brief as a readable document, and the rules Noodle turned it into.
 *
 * This tab absorbs what used to be a separate "Briefing" tab: the product, the kanban status and
 * the Activate action all live in the sidebar now, so there is one place to read the brief.
 */
@Component({
  selector: 'scr-screen-brief-management',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LdgImgComponent, DatePipe, ReviewQuestionsModal, ReviewQuestionsDrawer],
  templateUrl: './screen-brief-management.html',
  styleUrl: './screen-brief-management.scss',
})
export class ScreenBriefManagement {

  readonly ScreeningKanBanStatusEnum = ScreeningKanBanStatusEnum;

  readonly results = inject(ScreeningResultsService);
  readonly imageService = inject(ImageService);
  private readonly videoAiModerate = inject(VideoAiModerateService);
  private readonly reviewModal = viewChild(ReviewQuestionsModal);
  private readonly reviewDrawer = viewChild(ReviewQuestionsDrawer);

  private readonly subscriptions = new Subscription();

  readonly ruleFilter = signal<RuleFilter>('all');
  readonly isActivating = signal<boolean>(false);
  readonly brandSafetyOpen = signal<boolean>(false);
  readonly deliverablesOpen = signal<boolean>(false);

  readonly status = computed(() => this.results.kanban()?.status);

  readonly briefTitle = computed(() => this.results.brief()?.briefTitle?.trim() || 'Untitled brief');

  readonly requirementCount = computed(() => this.results.rules().length);

  readonly generalisedCount = computed(
    () => this.results.rules().filter((rule) => rule.generalised).length,
  );

  readonly pendingGeneralisedCount = computed(
    () => this.results.rules().filter((rule) => rule.generalised && !this.results.hasQuestionContext(rule)).length,
  );

  readonly contextReady = computed(
    () => this.generalisedCount() > 0 && this.pendingGeneralisedCount() === 0,
  );

  readonly awaitingProcessing = computed(() => {
    const status = this.status();
    return status === ScreeningKanBanStatusEnum.Not_Ready || status === ScreeningKanBanStatusEnum.Processing;
  });

  readonly canActivate = computed(() => {
    const status = this.status();
    return (
      status !== undefined &&
      status !== ScreeningKanBanStatusEnum.Not_Ready &&
      status !== ScreeningKanBanStatusEnum.Processing &&
      status !== ScreeningKanBanStatusEnum.Screening &&
      status !== ScreeningKanBanStatusEnum.Complete
    );
  });

  /** Shown when the activate action is not available for the brief's current status. */
  readonly activateUnavailableMessage = computed(() => {
    switch (this.status()) {
      case ScreeningKanBanStatusEnum.Not_Ready:
        return "This brief hasn't been sent for processing yet.";
      case ScreeningKanBanStatusEnum.Processing:
        return 'SHOUT AI is still reading this brief.';
      case ScreeningKanBanStatusEnum.Screening:
        return 'Screening is already underway.';
      case ScreeningKanBanStatusEnum.Complete:
        return 'Screening for this brief is complete.';
      default:
        return "This brief isn't ready to activate.";
    }
  });

  /** The first deliverable is the brief's own description of what was asked for. */
  readonly deliverable = computed(() => this.results.deliverables()[0]);

  readonly filterChips = computed<RuleFilterChip[]>(() => {
    const counts = this.results.ruleCounts();
    return [
      { id: 'all', label: 'All rules', count: counts.all },
      { id: 'do', label: "Do's", count: counts.dos },
      { id: 'dont', label: "Don'ts", count: counts.donts },
      // { id: 'objective', label: 'Objective', count: counts.objective },
      // { id: 'subjective', label: 'Subjective', count: counts.subjective },
    ];
  });

  /** Rules keep their position from the full list so the "#03" index stays stable when filtered. */
  readonly numberedRules = computed(() =>
    this.results.rules().map((rule, index) => ({ rule, number: index + 1 })),
  );

  readonly visibleRules = computed(() => {
    const filter = this.ruleFilter();
    return this.numberedRules().filter(({ rule }) => {
      switch (filter) {
        case 'do':
        case 'dont':
          return rule.type === filter;
        case 'objective':
          return rule.subjectiveType === 0;
        case 'subjective':
          return rule.subjectiveType === 1;
        default:
          return true;
      }
    });
  });

  readonly ruleSummary = computed(() => {
    const counts = this.results.ruleCounts();
    return `We've extracted ${counts.all} checkable rules from the brief — ${counts.dos} things creators must do and ${counts.donts} things they must not. Every piece of content is scored against all of them.`;
  });

  /** The user's own red flags from the brief wizard, grouped the way they built them. */
  readonly restrictionGroups = computed<RestrictionGroup[]>(() => {
    const restrictions = this.results.brief()?.restrictions ?? [];
    const groups = new Map<string, ScreeningRestrictionItem[]>();
    for (const restriction of restrictions) {
      const label = restriction.custom ? 'Custom' : restriction.categoryLabel;
      groups.set(label, [...(groups.get(label) ?? []), restriction]);
    }
    return [...groups.entries()].map(([label, items]) => ({ label, items }));
  });

  readonly reportTypeLabel = computed(() =>
    this.results.brief()?.reportType === 'custom' ? 'Custom analysis' : 'Standard report',
  );

  readonly reportTypeBody = computed(() =>
    this.results.brief()?.reportType === 'custom'
      ? 'A written spec you provided shapes how the results are presented back to you.'
      : 'Verdict per creator, every rule matched or breached with the evidence attached, and a full CSV export.',
  );

  /** The brief's screen-from day. */
  readonly screenFrom = computed(() => this.parseBriefDate(this.results.brief()?.screenUntil));

  /** Screening finishes on the day the brief was created. */
  readonly screenEnded = computed(() => this.parseBriefDate(this.results.brief()?.createdDate));

  readonly endsToday = computed(() => {
    const ended = this.screenEnded();
    return !!ended && startOfLocalDay(ended).getTime() === startOfLocalDay(new Date()).getTime();
  });

  readonly fromLabel = computed(() => {
    const from = this.screenFrom();
    return from ? formatScreeningDay(from) : '';
  });

  readonly toLabel = computed(() => {
    const ended = this.screenEnded();
    return ended ? formatScreeningDay(ended) : '';
  });

  /** "Today" only when the brief was created today; otherwise the created date. */
  readonly endMarkerLabel = computed(() => (this.endsToday() ? 'Today' : this.toLabel()));

  readonly windowCaption = computed(() => {
    const through = this.endsToday() ? 'today' : this.toLabel();
    return through ? `Posts were screened from this date through ${through}.` : '';
  });

  readonly yearAgoLabel = computed(() => {
    const ended = this.screenEnded();
    return ended ? formatScreeningDay(oneYearBefore(ended)) : '';
  });

  readonly dayCount = computed(() => {
    const from = this.screenFrom();
    const ended = this.screenEnded();
    return from && ended ? daysBetween(from, ended) : null;
  });

  readonly fill = computed(() => {
    const from = this.screenFrom();
    const ended = this.screenEnded();
    return from && ended ? timelineOffset(from, ended) : { left: 100, width: 0 };
  });

  setFilter(filter: RuleFilter): void {
    this.ruleFilter.set(filter);
  }

  /** Same selected/idle chrome as the screening tab bar. */
  filterChipClass(id: RuleFilter): string {
    return id === this.ruleFilter()
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  filterCountClass(id: RuleFilter): string {
    return id === this.ruleFilter()
      ? 'bg-gray-300 text-gray-900 dark:bg-white/20 dark:text-gray-200'
      : 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400';
  }

  /** Count sits in an aria-hidden pill, so the button name still includes it. */
  filterAccessibleLabel(chip: RuleFilterChip): string {
    return `${chip.label}, ${chip.count}`;
  }

  toggleBrandSafety(): void {
    this.brandSafetyOpen.update((open) => !open);
  }

  toggleDeliverables(): void {
    this.deliverablesOpen.update((open) => !open);
  }

  ruleTag(rule: NoodleQuestionDto): string {
    return rule.generalised ? 'Library rule' : 'Brief-specific';
  }

  ruleNumberLabel(index: number): string {
    return `#${String(index).padStart(2, '0')}`;
  }

  hasContext(rule: NoodleQuestionDto): boolean {
    return this.results.hasQuestionContext(rule);
  }

  activateBrief(): void {
    if (this.pendingGeneralisedCount() > 0) {
      this.reviewModal()?.open();
      return;
    }
    this.confirmActivate();
  }

  /** Skip on the review modal continues the activate flow. */
  onReviewSkipped(): void {
    this.confirmActivate();
  }

  onReviewRequested(): void {
    this.reviewDrawer()?.open();
  }

  onContextSaved(questions: readonly NoodleQuestionDto[]): void {
    this.results.applySavedQuestions(questions);
  }

  private parseBriefDate(raw: Date | string | null | undefined): Date | null {
    if (!raw) {
      return null;
    }
    const date = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private confirmActivate(): void {
    const briefId = this.results.briefId();
    if (!briefId || this.isActivating()) {
      return;
    }
    this.isActivating.set(true);
    this.subscriptions.add(
      this.videoAiModerate.approveBriefQuestions(briefId).subscribe({
        next: () => {
          this.isActivating.set(false);
          // Re-read the brief so the kanban status comes from the server, not a local mutation.
          this.results.load(briefId, true);
        },
        error: () => this.isActivating.set(false),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
